/**
 * Account-deletion request card on the Account page.
 *
 * States:
 *   - No request: shows the "Delete account" CTA, lists current blockers
 *     (active loans, open positions, positive balance) inline so the
 *     user knows what to clear first.
 *   - PENDING:    shows "Cancel deletion request" + countdown to purge.
 *   - PURGED:     UI is unreachable — the user can no longer log in.
 *
 * Backend reference: POST/GET/DELETE /api/me/delete-request/
 */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Modal, Input, message, Alert } from "antd";
import {
  TrashOutline,
  AlertCircleOutline,
  TimeOutline,
} from "@raresail/react-ionicons";
import useGet from "../../../hooks/common/useGet";
import useSet from "../../../hooks/common/useSet";
import useAxios from "../../../hooks/common/useAxios";
import { API_DELETE_REQUEST } from "../../../constants/apis";

interface DeletionState {
  status: "pending" | "cancelled" | "purged" | null;
  reason?: string;
  requested_at?: string;
  purge_eligible_at?: string;
  days_until_eligible?: number;
  blockers?: string[];
}

const { TextArea } = Input;

export const DeleteAccountCard = () => {
  const [state, setState] = useState<DeletionState | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { loadData, loading } = useGet();
  const { uploadData, submitting } = useSet();
  const { set } = useAxios();
  const [cancelling, setCancelling] = useState(false);

  const refresh = () => {
    loadData({
      api: API_DELETE_REQUEST,
      onSuccess: (data: any) => setState(data),
    });
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render the card immediately (don't hide on initial load) so users
  // always have a visible delete entry-point. We just disable the CTA
  // until the status check resolves.
  const isPending = state?.status === "pending";
  const blockers = state?.blockers || [];
  const statusUnknown = loading && !state;

  const submitRequest = () => {
    uploadData({
      api: API_DELETE_REQUEST,
      params: { reason },
      method: "POST",
      successMessage: { message: "Deletion request submitted", duration: 4 },
      onSuccess: (data: any) => {
        setState(data);
        setModalOpen(false);
        setReason("");
      },
    });
  };

  const cancel = () => {
    setCancelling(true);
    // useAxios.set is a generic mutator — pass methodName: "DELETE" so we
    // hit the cancel endpoint. (`useSet` hardcodes PATCH/POST, hence the
    // direct call here.)
    set({
      api: API_DELETE_REQUEST,
      methodName: "DELETE",
      loader: setCancelling,
      successHandler: (data: any) => {
        message.success("Deletion request cancelled");
        setState(data);
      },
      errorHandler: () => {
        message.error("Couldn't cancel — please try again");
      },
      successMessage: false,
    });
  };

  if (isPending) {
    return (
      <Card style={{ borderColor: "#fda4af" }}>
        <Header>
          <IconBubble style={{ background: "#fee2e2" }}>
            <TimeOutline fontSize={20} color="#b91c1c" />
          </IconBubble>
          <div className="flexGrow">
            <div className="font14 semiBold" style={{ color: "#b91c1c" }}>
              Account deletion pending
            </div>
            <div className="font12 grey" style={{ lineHeight: 1.4 }}>
              We'll permanently delete your account in{" "}
              <b>{state?.days_until_eligible ?? 30} day(s)</b>. You can cancel
              any time before then.
            </div>
          </div>
          <Button danger loading={cancelling} onClick={cancel}>
            Cancel request
          </Button>
        </Header>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Header>
          <IconBubble>
            <TrashOutline fontSize={20} color="#475569" />
          </IconBubble>
          <div className="flexGrow">
            <div className="font14 semiBold textDark">Delete account</div>
            <div className="font12 grey" style={{ lineHeight: 1.4 }}>
              Request permanent deletion. An admin will purge your data after
              a 30-day cool-off — you can cancel any time in between.
            </div>
          </div>
          <Button
            danger
            type="default"
            disabled={blockers.length > 0 || statusUnknown}
            loading={statusUnknown}
            onClick={() => setModalOpen(true)}
          >
            Delete account
          </Button>
        </Header>

        {blockers.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<AlertCircleOutline color="#92400e" />}
            style={{ marginTop: 12 }}
            message="Settle these before requesting deletion:"
            description={
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            }
          />
        )}
      </Card>

      <Modal
        title="Delete your Everpesa account?"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Keep my account
          </Button>,
          <Button
            key="confirm"
            danger
            type="primary"
            loading={submitting}
            onClick={submitRequest}
          >
            Submit deletion request
          </Button>,
        ]}
      >
        <p className="font13" style={{ lineHeight: 1.6 }}>
          We'll keep your account locked for <b>30 days</b> so you can change
          your mind. After that, an admin will permanently anonymize your
          personal data. Your transaction records are kept for audit but
          stripped of identifying info.
        </p>
        <p className="font12 grey">
          Tell us why you're leaving (optional — helps us improve):
        </p>
        <TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional feedback"
        />
      </Modal>
    </>
  );
};

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const IconBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;
